import { useState } from 'react'
import { Card, Form, Row, Col, Button, Modal, Alert, Badge } from 'react-bootstrap'
import { useAppStore } from '../stores/appStore'

const UserGroupManager = () => {
  const { 
    addUserGroup, 
    updateUserGroup, 
    deleteUserGroup,
    getRuntimeData
  } = useAppStore()
  const runtimeData = getRuntimeData()
  const effectiveAccounts = runtimeData.accounts || {}
  const effectiveUserGroups = runtimeData.userGroups || {}
  
  const [showModal, setShowModal] = useState(false)
  const [editingGroup, setEditingGroup] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    selectedAccounts: []
  })

  const handleCreateGroup = () => {
    setEditingGroup(null)
    setFormData({
      name: '',
      description: '',
      selectedAccounts: []
    })
    setShowModal(true)
  }

  const handleEditGroup = (group) => {
    setEditingGroup(group)
    setFormData({
      name: group.name,
      description: group.description,
      selectedAccounts: group.selectedAccounts
    })
    setShowModal(true)
  }

  const handleSaveGroup = () => {
    if (!formData.name.trim()) {
      alert('请输入用户组名称')
      return
    }

    if (formData.selectedAccounts.length === 0) {
      alert('请至少选择一个账号')
      return
    }

    const groupData = {
      name: formData.name,
      description: formData.description,
      selectedAccounts: formData.selectedAccounts,
      createdAt: editingGroup ? editingGroup.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    if (editingGroup) {
      updateUserGroup(editingGroup.id, groupData)
    } else {
      addUserGroup(groupData)
    }

    setShowModal(false)
  }

  const handleAccountToggle = (accountKey) => {
    const newSelection = formData.selectedAccounts.includes(accountKey)
      ? formData.selectedAccounts.filter(key => key !== accountKey)
      : [...formData.selectedAccounts, accountKey]
    
    setFormData(prev => ({
      ...prev,
      selectedAccounts: newSelection
    }))
  }

  const getServerColor = (server) => {
    const colors = ['primary', 'success', 'info', 'warning', 'danger', 'secondary']
    const hash = String(server || '').split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)
    return colors[hash % colors.length]
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>用户组管理</h5>
        <Button variant="primary" onClick={handleCreateGroup}>
          创建用户组
        </Button>
      </div>

      {/* 用户组列表 */}
      <Row className="g-3">
        {Object.entries(effectiveUserGroups).map(([groupId, group]) => (
          <Col key={groupId} md={6} lg={4}>
            <Card className="h-100">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">{group.name}</h6>
                <div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-1"
                    onClick={() => handleEditGroup({ ...group, id: groupId })}
                  >
                    编辑
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => {
                      if (confirm(`确定要删除用户组"${group.name}"吗？`)) {
                        deleteUserGroup(groupId)
                      }
                    }}
                  >
                    删除
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <p className="text-muted small mb-2">{group.description}</p>
                <div className="mb-2">
                  <Badge bg="info">{group.selectedAccounts.length} 个账号</Badge>
                </div>
                <div className="mb-2">
                  {group.selectedAccounts.slice(0, 3).map(accountKey => {
                    const account = effectiveAccounts[accountKey]
                    if (!account) return null
                    return (
                      <Badge 
                        key={accountKey} 
                        bg={getServerColor(account.server)} 
                        className="me-1 mb-1"
                      >
                        {account.username} {account.server}
                      </Badge>
                    )
                  })}
                  {group.selectedAccounts.length > 3 && (
                    <Badge bg="secondary">+{group.selectedAccounts.length - 3}</Badge>
                  )}
                </div>
                <div className="small text-muted">
                  创建时间: {new Date(group.createdAt).toLocaleString()}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {Object.keys(effectiveUserGroups).length === 0 && (
        <Alert variant="info" className="text-center">
          暂无用户组，点击&quot;创建用户组&quot;开始创建
        </Alert>
      )}

      {/* 创建/编辑用户组模态框 */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingGroup ? '编辑用户组' : '创建用户组'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>用户组名称</Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="输入用户组名称"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>描述</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="输入用户组描述"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>选择账号</Form.Label>
            <div className="border rounded p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {Object.entries(effectiveAccounts).length === 0 ? (
                <Alert variant="warning">暂无可用账号</Alert>
              ) : (
                Object.entries(effectiveAccounts).map(([accountKey, account]) => (
                  <div
                    key={accountKey}
                    className={`border rounded p-2 mb-2 d-flex align-items-center ${
                      formData.selectedAccounts.includes(accountKey) ? 'border-primary bg-primary bg-opacity-10' : 'border-light'
                    }`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleAccountToggle(accountKey)}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={formData.selectedAccounts.includes(accountKey)}
                      onChange={() => handleAccountToggle(accountKey)}
                      onClick={(e) => e.stopPropagation()}
                      className="me-3"
                    />
                    <div className="flex-grow-1">
                      <div className="fw-medium">{account.username}</div>
                      <div className="small text-muted">
                        服务器: {account.server}
                        {account.userInfo && (
                          <span className="ms-2">
                            等级: {account.userInfo.level} | 
                            VIP: {account.userInfo.vip}
                          </span>
                        )}
                      </div>
                    </div>
                    <Badge bg={getServerColor(account.server)}>
                      {account.server}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </Form.Group>

          {formData.selectedAccounts.length > 0 && (
            <Alert variant="info">
              已选择 {formData.selectedAccounts.length} 个账号
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSaveGroup}>
            {editingGroup ? '更新' : '创建'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default UserGroupManager
